import * as React from "react"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import {
  Button,
  ThemeCss,
  CheckIcon,
  Link,
  Notification,
  Heading,
  Text,
  CopyButton,
  InProgressIcon,
} from "gatsby-interface"
import { MdWarning, MdError } from "react-icons/md"
import { FormattedMessage } from "@modules/locales"
import { HostingDomain } from "@modules/graphql/types"

const certCss: ThemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
  flexShrink: 0,
})

const checkIconCss: ThemeCss = theme => ({
  color: theme.colors.green[70],
  flexShrink: 0,
})

export function HttpsOnBadge() {
  return (
    <span css={certCss}>
      <CheckIcon css={checkIconCss} /> {text.labels.httpsOn}
    </span>
  )
}

export function GenerationFailed() {
  return <span css={certCss}>{text.labels.httpsFailed}</span>
}

const inProgressIconCss: ThemeCss = theme => ({
  color: theme.colors.purple[60],
  margin: `0 ${theme.space[3]}`,
})

export function GeneratingCertBadge({ message }: { message: string }) {
  return (
    <span css={certCss}>
      <InProgressIcon css={inProgressIconCss} /> {message}
    </span>
  )
}

const notificationTitleCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[1],
  fontFamily: theme.fonts.body,
  fontWeight: theme.fontWeights.body,
  color: theme.colors.grey[90],
  marginTop: 0,
  marginBottom: theme.space[2],

  strong: {
    fontWeight: theme.fontWeights.semiBold,
    color: theme.colors.grey[70],
  },
})

const notificationMessageCss: ThemeCss = theme => ({
  marginBottom: theme.space[3],
  marginRight: theme.space[3],
  fontSize: theme.fontSizes[0],
})

const instructionDetailsCss: ThemeCss = theme => ({
  display: `grid`,
  gridTemplateColumns: `1fr`,
  gap: theme.space[10],
  overflow: `auto`,

  table: {
    fontSize: theme.fontSizes[0],
    margin: 0,
  },

  "td, th": {
    border: 0,
    padding: 0,
    paddingTop: theme.space[1],
    fontWeight: theme.fontWeights.body,
  },

  "td:nth-of-type(1)": {
    paddingLeft: theme.space[3],
    paddingRight: theme.space[3],
  },

  thead: {
    th: {
      fontSize: theme.fontSizes[0],
      fontWeight: theme.fontWeights.semiBold,
    },
  },

  [theme.mediaQueries.tablet]: {
    gridTemplateColumns: `1fr`,
  },
})

const notificationCss: ThemeCss = theme => ({
  marginBottom: theme.space[5],
  marginTop: `-${theme.space[2]}`,

  "& > span": {
    width: `100%`,
  },
})

const warningIconCss: ThemeCss = theme => ({
  "&&": {
    color: theme.colors.orange[60],
    marginRight: theme.space[3],
    fontSize: `1.2em`,
  },
})

const topCss: ThemeCss = theme => ({
  marginBottom: theme.space[5],
})

const headerCss: ThemeCss = theme => ({
  display: `flex`,
  justifyContent: `space-between`,
  alignItems: `flex-start`,
  flexWrap: `wrap`,
})

const checkButtonCss: ThemeCss = theme => ({
  marginBottom: theme.space[3],
})

const warningsCss: ThemeCss = theme => ({
  display: `flex`,
})

const copyButtonCss: ThemeCss = theme => ({
  marginLeft: theme.space[2],

  /* @TODO: extend CopyButton api, the left/right 0 corners should be optionl */
  "html:not([dir=rtl]) &": {
    borderTopLeftRadius: theme.radii[2],
    borderBottomLeftRadius: theme.radii[2],
  },
  "html[dir=rtl] &": {
    borderTopRightRadius: theme.radii[2],
    borderBottomRightRadius: theme.radii[2],
  },
})

const recordIdCss: ThemeCss = theme => ({
  whiteSpace: `nowrap`,
})

export function WarningIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return <MdWarning {...props} css={warningIconCss} />
}

export type DnsSetupInstructionProps = {
  primaryDomain: HostingDomain
  secondaryDomain?: HostingDomain
  validating: boolean
  onValidate: (e: React.MouseEvent) => void
  allowToEdit?: boolean
}

export function DnsSetupInstruction({
  primaryDomain,
  secondaryDomain,
  validating,
  onValidate,
  allowToEdit,
}: DnsSetupInstructionProps) {
  const dns: { id: string; type: string; name: string }[] = []

  if (!primaryDomain.verified) {
    primaryDomain?.dns?.forEach(({ id, type }) =>
      dns.push({ id, type, name: primaryDomain.id })
    )
  }
  if (!secondaryDomain?.verified) {
    secondaryDomain?.dns?.forEach(({ id, type }) =>
      dns.push({ id, type, name: secondaryDomain.id })
    )
  }

  return (
    <Notification
      variant="SECONDARY"
      tone="NEUTRAL"
      css={notificationCss}
      content={
        <div css={{ width: `100%` }}>
          <div css={topCss}>
            <div css={headerCss}>
              <div>
                <Heading css={notificationTitleCss} as="h5">
                  {text.messages.pleaseUpdateDnsRecord}
                </Heading>
                <Text css={notificationMessageCss}>
                  <FormattedMessage<never, "link">
                    message={text.messages.checkStatus}
                    tags={{
                      link: function(content) {
                        return (
                          <Link to="https://support.gatsbyjs.com/hc/en-us/articles/360063469873-Adding-a-Custom-Domain">
                            {content}
                          </Link>
                        )
                      },
                    }}
                  />
                </Text>
              </div>
              {allowToEdit && (
                <Button
                  size="S"
                  disabled={validating}
                  onClick={onValidate}
                  css={checkButtonCss}
                >
                  {text.actions.checkStatus}
                </Button>
              )}
            </div>
            <div css={warningsCss}>
              {primaryDomain.verificationMessage && (
                <Text css={notificationMessageCss}>
                  <WarningIcon />
                  {primaryDomain.verificationMessage}
                </Text>
              )}
              {secondaryDomain?.verificationMessage && (
                <Text css={notificationMessageCss}>
                  <WarningIcon />
                  {secondaryDomain.verificationMessage}
                </Text>
              )}
            </div>
          </div>
          <div css={instructionDetailsCss}>
            <table>
              <thead>
                <tr>
                  <th>domain</th>
                  <th>{text.labels.type}</th>
                  <th>{text.labels.value}</th>
                </tr>
              </thead>
              <tbody>
                {dns.map(record => {
                  return (
                    <tr key={record.id}>
                      <th>{record.name}</th>
                      <td>{record.type}</td>
                      <td css={recordIdCss}>
                        {record.id}{" "}
                        <CopyButton
                          content={record.id}
                          aria-label="Copy record value"
                          css={copyButtonCss}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      }
    />
  )
}

const errorIconCss: ThemeCss = theme => ({
  "&&": {
    color: theme.colors.red[80],
  },
})

function ErrorIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return <MdError {...props} css={errorIconCss} />
}

export type DomainErrorMessageProps = {
  message: string
}

export function DomainErrorMessage({ message }: DomainErrorMessageProps) {
  return (
    <Notification
      css={notificationCss}
      variant="SECONDARY"
      tone="NEUTRAL"
      content={
        <div>
          <Heading css={notificationTitleCss} as="h5">
            {text.messages.errorOccured}
          </Heading>
          <Text css={notificationMessageCss}>{message}</Text>
        </div>
      }
      Icon={ErrorIcon}
    />
  )
}
